import { useCustomerCtx, useDialogCtx } from '../../../hooks/context-hooks'
import { deviationsEndpoints } from '../../../api/deviation'
import { useTranslation } from 'react-i18next'
import { Box, useTheme, Card, TextField, Chip, MenuItem } from '@mui/material'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import GenericTable from 'src/components/table/GenericTable'
import { useState, useMemo, useCallback } from 'react'
import { IDeviationStats } from 'src/generated-types/deviation-admin'
import { deviationQueryKeys } from 'src/query-keys'
import { useGet } from 'src/api/reactQuery'
import DeviationDetailDialog from 'src/dialogs/DeviationDetailDialog'
import { DialogContextType } from 'src/enums'
import PieChartCard from 'src/components/charts/PieChartCard'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { useGetLocalityGroups } from 'src/api/locality-groups'
import { APIResultCount } from 'src/constant'

const DeviationsAdminScreen = () => {
  const { customer } = useCustomerCtx()
  const { t: translate, i18n } = useTranslation(['common', 'deviation'])
  const theme = useTheme()

  const [selectedLocalityId, setSelectedLocalityId] = useState<number | null>(null)
  const [selectedLocalityGroupId, setSelectedLocalityGroupId] = useState<number | null>(null)
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>('all')

  const customerId = customer?.id as number
  const { showDialog } = useDialogCtx()

  const { data: localities } = useGet<Array<{ id: number; name: string }>>(`customers/${customerId}/localities/names`)
  const { data: localityGroups } = useGetLocalityGroups(customerId, 0, APIResultCount.Max)

  const queryParams = useMemo(
    () => ({
      ...(selectedLocalityId && { localityId: selectedLocalityId }),
      ...(selectedLocalityGroupId && { localityGroupId: selectedLocalityGroupId }),
      ...(dateFrom && { dateFrom: dateFrom.toISOString() }),
      ...(dateTo && { dateTo: dateTo.toISOString() }),
      ...(searchText && { search: searchText }),
      ...(statusFilter !== 'all' && { isOpen: statusFilter === 'open' }),
    }),
    [selectedLocalityId, selectedLocalityGroupId, dateFrom, dateTo, searchText, statusFilter],
  )

  const { data: stats } = useGet<IDeviationStats>(deviationsEndpoints.getAdminDeviationStats(customerId), queryParams, {
    queryKey: deviationQueryKeys.GET_ADMIN_DEVIATION_STATS(customerId, queryParams),
  })

  const generateQueryKey = useCallback(
    (page: number, pageSize: number) =>
      deviationQueryKeys.GET_ADMIN_DEVIATIONS_LIST(customerId, { ...queryParams, page, pageSize }),
    [customerId, queryParams],
  )

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: '#',
        flex: 0.5,
        minWidth: 70,
      },
      {
        field: 'title',
        headerName: translate('title', { ns: 'common' }),
        flex: 3,
        minWidth: 200,
      },
      {
        field: 'connection',
        headerName: translate('connection', { ns: 'common' }),
        flex: 2,
        minWidth: 150,
        renderCell: (params: any) => {
          if (!params.row.connectionType) return '--'
          return `${translate(params.row.connectionType, { ns: 'origin-names' })} | ${
            params.row.connectionName || '--'
          }`
        },
      },
      {
        field: 'localityName',
        headerName: translate('locality', { ns: 'common' }),
        flex: 2,
        minWidth: 150,
      },
      {
        field: 'isOpen',
        headerName: translate('status', { ns: 'common' }),
        flex: 1,
        minWidth: 100,
        renderCell: (params: any) => (
          <Chip
            label={params.row.isOpen ? translate('open', { ns: 'common' }) : translate('closed', { ns: 'common' })}
            color={params.row.isOpen ? 'error' : 'success'}
            size="small"
          />
        ),
      },
      {
        field: 'priority',
        headerName: translate('priority', { ns: 'common' }),
        flex: 1,
        minWidth: 100,
        renderCell: (params: any) => {
          const priorityKey = String(params.row.priority || '').toLowerCase()
          const label = translate(priorityKey, { ns: 'common', defaultValue: params.row.priority })
          return <Chip label={label} color={params.row.priority === 'Critical' ? 'error' : 'default'} size="small" />
        },
      },
      {
        field: 'createdAt',
        headerName: translate('created-at', { ns: 'common' }),
        flex: 2,
        minWidth: 150,
        renderCell: (params: any) => {
          const date = new Date(params.row.createdAt)
          return getFormattedDate(date)
        },
      },
      {
        field: 'createdByUserName',
        headerName: translate('created-by', { ns: 'common' }),
        flex: 2,
        minWidth: 150,
      },
    ],
    [translate, i18n.language],
  )

  const handleRowClick = useCallback(
    (params: any) => {
      showDialog(
        DeviationDetailDialog,
        {
          componentProps: { customerId, deviationId: params.id },
          opts: { maxWidth: 'xl', fullWidth: true },
          title: translate('deviation-details', { ns: 'deviation' }),
        },
        DialogContextType.customerAdminContext,
        true,
      )
    },
    [customerId, showDialog, translate],
  )

  // Prepare chart data
  const statusChartData = useMemo(() => {
    if (!stats) return []
    return [
      {
        label: translate('open-deviations', { ns: 'deviation' }),
        value: stats.openCount,
        color: theme.palette.error.main,
      },
      {
        label: translate('closed-deviations', { ns: 'deviation' }),
        value: stats.closedCount,
        color: theme.palette.success.main,
      },
    ]
  }, [stats, translate, theme])

  const connectionChartData = useMemo(() => {
    if (!stats || !stats.connectionTypeCounts) return []
    return Object.entries(stats.connectionTypeCounts).map(([type, count]) => ({
      label: translate(type, { ns: 'deviation', defaultValue: type }),
      value: count,
    }))
  }, [stats, translate])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <CommonPageLayout titleSection={{ title: translate('deviations', { ns: 'deviation' }) }}>
        {/* Filters */}
        <Card sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Box flex={1} minWidth={220}>
              <TextField
                select
                fullWidth
                size="small"
                label={translate('locality', { ns: 'common' })}
                value={selectedLocalityId ?? ''}
                onChange={e => {
                  setSelectedLocalityId(e.target.value ? parseInt(e.target.value, 10) : null)
                }}
              >
                <MenuItem value="">{translate('all', { ns: 'common' })}</MenuItem>
                {localities?.map(locality => (
                  <MenuItem key={locality.id} value={locality.id}>
                    {locality.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box flex={1} minWidth={220}>
              <TextField
                select
                fullWidth
                size="small"
                label={translate('location-group', { ns: 'common' })}
                value={selectedLocalityGroupId ?? ''}
                onChange={e => {
                  setSelectedLocalityGroupId(e.target.value ? parseInt(e.target.value, 10) : null)
                }}
              >
                <MenuItem value="">{translate('all', { ns: 'common' })}</MenuItem>
                {localityGroups?.items?.map(group => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box flex={1} minWidth={220}>
              <DatePicker
                label={translate('date-from', { ns: 'deviation' })}
                value={dateFrom}
                onChange={(newValue: any) => setDateFrom(newValue)}
                renderInput={params => <TextField {...params} fullWidth size="small" />}
              />
            </Box>

            <Box flex={1} minWidth={220}>
              <DatePicker
                label={translate('date-to', { ns: 'deviation' })}
                value={dateTo}
                onChange={(newValue: any) => setDateTo(newValue)}
                renderInput={params => <TextField {...params} fullWidth size="small" />}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box flex={2} minWidth={300}>
              <TextField
                fullWidth
                size="small"
                label={translate('search', { ns: 'deviation' })}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder={translate('search-title-description-reason', { ns: 'deviation' })}
              />
            </Box>

            <Box flex={1} minWidth={200} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={translate('all', { ns: 'common' })}
                onClick={() => setStatusFilter('all')}
                color={statusFilter === 'all' ? 'primary' : 'default'}
                variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip
                label={translate('open', { ns: 'common' })}
                onClick={() => setStatusFilter('open')}
                color={statusFilter === 'open' ? 'error' : 'default'}
                variant={statusFilter === 'open' ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip
                label={translate('closed', { ns: 'common' })}
                onClick={() => setStatusFilter('closed')}
                color={statusFilter === 'closed' ? 'success' : 'default'}
                variant={statusFilter === 'closed' ? 'filled' : 'outlined'}
                size="small"
              />
            </Box>
          </Box>
        </Card>

        {/* Chart Row */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
            <PieChartCard
              title={translate('status', { ns: 'common' })}
              data={statusChartData}
              size="medium"
              variant="plain"
            />
          </Box>
          <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
            <PieChartCard
              title={translate('connection-type', { ns: 'deviation' })}
              data={connectionChartData}
              size="medium"
              variant="plain"
            />
          </Box>
          <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
            <PieChartCard title="Reserved" data={[]} size="medium" variant="plain" />
          </Box>
        </Box>

        <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
          <GenericTable
            id="admin-deviations-table"
            columns={columns}
            enableServerPagination
            enablePagination
            pageSize={50}
            pageOptions={[10, 20, 50]}
            queryUrl={deviationsEndpoints.getAdminDeviationsList(customerId)}
            queryParams={queryParams}
            generateQueryKey={generateQueryKey}
            onRowClick={handleRowClick}
            noRowsMessage={translate('no-deviations-found', { ns: 'deviation' })}
            autoHeight={true}
            minRowHeight="36px"
            getRowHeight={() => 40}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                minHeight: '32px !important',
                maxHeight: '32px !important',
              },
              '& .MuiDataGrid-columnHeader': {
                py: 0.5,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: '12px',
                fontWeight: 500,
              },
            }}
          />
        </Box>
      </CommonPageLayout>
    </LocalizationProvider>
  )
}

export default DeviationsAdminScreen
